/**
 * Flat, square shared UI primitives for the labeling-overview redesign.
 *
 * Every primitive here is presentational only (no data fetching, no atoms).
 * They mirror the approved mockup's `.tag2` / `.meter` / `.stock` / `.list`
 * / `.section-lab` / `.countstrip` classes 1:1 in Tailwind, and copy the
 * exact idiom already shipped in `ReportingStudyHome.tsx` (the house style
 * reference: `panel-premium rounded border border-border/50 divide-y
 * divide-border/50` container, `w-full flex items-center gap-4 px-5 py-4`
 * rows, `px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded
 * border` badges).
 *
 * NON-NEGOTIABLE: radius is `rounded` (2px) or `rounded-sharp` only, never
 * `rounded-full` / `rounded-lg` / `rounded-xl`. This is the whole point of
 * the flat-style redesign — see PLANS/reviews/2026-07-07-labeling-overview-
 * redesign-mockup.html.
 */

import React from "react";

// ---------------------------------------------------------------------------
// StatusBadge — mirrors mockup `.tag2` / `.tag2.sig` / `.tag2.success` / …
// ---------------------------------------------------------------------------

// Task 15 (Andrea feedback): the overview tree's accent palette is
// restricted to blue / red / green ONLY — every other accent hue is gone.
// `sig` = blue (active/pending/informational), `success` = green
// (complete/high-agreement/GT), `warn` = red (blocked/error/low-agreement).
export type StatusTone = "neutral" | "sig" | "success" | "warn" | "ghost";

const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  neutral: "bg-surface-2 text-fg-muted border-border-strong",
  sig: "bg-primary/15 text-signal-text-tint border-signal-stroke/40",
  success: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  warn: "bg-warn-tint text-warn border-warn/30",
  ghost: "text-fg-dim border-border",
};

interface StatusBadgeProps {
  tone?: StatusTone;
  children: React.ReactNode;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ tone = "neutral", children }) => (
  <span
    className={`inline-flex items-center px-2 py-1 text-[10px] font-semibold uppercase tracking-wide rounded border ${STATUS_TONE_CLASSES[tone]}`}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// ProgressMeter — mirrors mockup `.meter` (flat square track, never a pill)
// ---------------------------------------------------------------------------

interface ProgressMeterProps {
  done: number;
  total: number;
  tone?: "active" | "done";
  /** Trailing label style: `"fraction"` (default, `"62 / 100"` — existing
   *  3D-seg factory usage is unchanged), `"percent"`
   *  (`Math.round(done/total*100)}%`, right-aligned mono), or `"none"`
   *  (bar only, no trailing label). The fill is always blue (`bg-primary`)
   *  regardless of `display` (Task 15). */
  display?: "fraction" | "percent" | "none";
}

export const ProgressMeter: React.FC<ProgressMeterProps> = ({
  done,
  total,
  tone = "active",
  display = "fraction",
}) => {
  const pct = total > 0 ? (done / total) * 100 : 0;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="block h-1 w-[70px] overflow-hidden rounded-sharp bg-white/[0.06]">
        <span
          data-testid="progress-meter-fill"
          className={`block h-full rounded-sharp ${tone === "done" ? "bg-fg-faint" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      {display !== "none" && (
        <span
          className={`font-mono text-[11.5px] text-fg-muted ${display === "percent" ? "min-w-[2.5em] text-right" : ""}`}
        >
          {display === "percent" ? `${Math.round(pct)}%` : `${done} / ${total}`}
        </span>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// PoolGauge — mirrors mockup `.stock` (full) / `.stock.pool-mini` (mini)
// ---------------------------------------------------------------------------

interface PoolGaugeProps {
  available: number;
  awaitingSecond: number;
  gt: number;
  variant?: "mini" | "full";
}

export const PoolGauge: React.FC<PoolGaugeProps> = ({
  available,
  awaitingSecond,
  gt,
  variant = "full",
}) => {
  const total = available + awaitingSecond + gt;
  const segmentPct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  const barHeight = variant === "mini" ? "h-2" : "h-3";

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={`flex ${barHeight} overflow-hidden rounded-sharp border border-border bg-white/[0.06]`}
      >
        <span className="block h-full bg-primary" style={{ width: `${segmentPct(available)}%` }} />
        <span
          className="block h-full bg-signal-stroke-soft"
          style={{ width: `${segmentPct(awaitingSecond)}%` }}
        />
        <span className="block h-full bg-fg-faint" style={{ width: `${segmentPct(gt)}%` }} />
      </div>
      {variant === "full" && (
        <div className="flex flex-wrap gap-3.5 text-[11px] text-fg-dim">
          <span>
            <b className="mr-0.5 font-mono font-medium text-fg">{available}</b> available
          </span>
          <span>
            <b className="mr-0.5 font-mono font-medium text-fg">{awaitingSecond}</b> awaiting 2nd
          </span>
          <span>
            <b className="mr-0.5 font-mono font-medium text-fg">{gt}</b> GT
          </span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SquareList / SquareRow — mirrors mockup `.list` + `.list .row`, exact
// idiom copied from ReportingStudyHome.tsx's `panel-premium` container/rows.
// ---------------------------------------------------------------------------

interface SquareListProps {
  children: React.ReactNode;
}

export const SquareList: React.FC<SquareListProps> = ({ children }) => (
  <div className="panel-premium rounded border border-border/50 divide-y divide-border/50">
    {children}
  </div>
);

interface SquareRowProps {
  onClick?: () => void;
  children: React.ReactNode;
}

const SQUARE_ROW_CLASSES = "w-full flex items-center gap-4 px-5 py-4 text-left";

export const SquareRow: React.FC<SquareRowProps> = ({ onClick, children }) => {
  if (onClick) {
    return (
      <button onClick={onClick} className={`${SQUARE_ROW_CLASSES} hover:bg-white/2 transition`}>
        {children}
      </button>
    );
  }
  return <div className={SQUARE_ROW_CLASSES}>{children}</div>;
};

// ---------------------------------------------------------------------------
// SectionLabel — mirrors mockup `.section-lab` + `.eyebrow` + `.n`
// ---------------------------------------------------------------------------

interface SectionLabelProps {
  eyebrow: string;
  note?: string;
}

export const SectionLabel: React.FC<SectionLabelProps> = ({ eyebrow, note }) => (
  <div className="mt-6 mb-2.5 flex items-baseline gap-2.5">
    <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-dim">
      {eyebrow}
    </span>
    {note && <span className="font-mono text-[11px] text-fg-faint">{note}</span>}
  </div>
);

// ---------------------------------------------------------------------------
// LoadingPanel — the flat-style loading state shared across overview lists
// and tables. Same box footprint as the sibling empty/error panels dotted
// through this tree (`rounded border border-border/50 bg-surface p-8
// text-center text-sm text-fg-muted`) so swapping between loading / empty /
// error / data never shifts layout. Plays the same role as
// ReportingStudyHome.tsx's loading spinner, but the spin glyph is a
// `rounded-sharp` square (never `rounded-full` — the flat-style rule this
// whole tree is built on).
// ---------------------------------------------------------------------------

interface LoadingPanelProps {
  label?: string;
}

export const LoadingPanel: React.FC<LoadingPanelProps> = ({ label = "Loading…" }) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
    <span
      aria-hidden="true"
      className="h-5 w-5 animate-spin rounded-sharp border-2 border-primary/30 border-t-primary"
    />
    <span>{label}</span>
  </div>
);

// ---------------------------------------------------------------------------
// CountStrip — mirrors mockup `.countstrip` (mono counts, dot separators)
// ---------------------------------------------------------------------------

interface CountStripProps {
  items: string[];
}

export const CountStrip: React.FC<CountStripProps> = ({ items }) => (
  <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted">
    {items.map((item, i) => (
      <span key={`${i}-${item}`} className="flex items-center gap-3">
        {i > 0 && (
          <span data-testid="countstrip-sep" className="text-fg-faint">
            ·
          </span>
        )}
        <span className="font-mono">{item}</span>
      </span>
    ))}
  </div>
);
